// src/components/TransactionInput.js
import React, { useState, useEffect, useContext } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

import { FirebaseContext } from '../App';
// FirebaseContextからfirebaseAppIdを取得するように変更するため、このimportは不要になります
// import { firebaseAppId } from '../configs/firebaseConfig'; 

const TransactionInput = ({ setCurrentPage }) => { // setCurrentPageはApp.jsからpropsで渡されます
    // FirebaseContextから必要な値を useContext で取得
    const { db, userId, firebaseAppId, isMobile, scannedReceiptData, setScannedReceiptData } = useContext(FirebaseContext); 
    
    // フォームの各入力フィールドの状態を個別に管理
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [type, setType] = useState('支出');
    const [category, setCategory] = useState('');
    const [amount, setAmount] = useState('');
    const [description, setDescription] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('現金');
    const [memo, setMemo] = useState('');
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('');

    // 勘定科目リスト
    const expenseCategories = ['仕入', '消耗品費', '交通費', '通信費', '水道光熱費', '地代家賃', '福利厚生費', '旅費交通費', '広告宣伝費', '接待交際費', '租税公課', '減価償却費', '修繕費', '新聞図書費', '会議費', '雑費', '事業主貸'];
    const incomeCategories = ['売上', '事業主借', '元入金', 'その他収入'];

    // レシートスキャンデータが渡されたらフォームに自動入力
    useEffect(() => {
        if (scannedReceiptData) {
            setDate(scannedReceiptData.date || new Date().toISOString().split('T')[0]);
            setAmount(scannedReceiptData.amount ? parseFloat(scannedReceiptData.amount).toFixed(2) : '');
            // OCRから取得したカテゴリが存在し、それが既存の勘定科目リストに含まれているか確認
            // 種類（type）も考慮してカテゴリをセット
            if (scannedReceiptData.category) {
                if (type === '支出' && expenseCategories.includes(scannedReceiptData.category)) {
                    setCategory(scannedReceiptData.category);
                } else if (type === '収入' && incomeCategories.includes(scannedReceiptData.category)) {
                    setCategory(scannedReceiptData.category);
                } else {
                    setCategory(''); // 存在しないか種類と合わない場合はリセット
                }
            } else {
                setCategory(''); // カテゴリが提供されない場合はリセット
            }
            setDescription(scannedReceiptData.description || ''); // 内容も自動入力対象
            
            setMessage('レシートから情報が自動入力されました。ご確認ください。');
            setMessageType('success');
            setScannedReceiptData(null); // 一度使用したらクリア
        }
    }, [scannedReceiptData, setScannedReceiptData, type]); // typeも依存配列に追加

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage('');

        if (!db || !userId) {
            setMessage('エラー: データベース接続またはユーザー情報がありません。');
            setMessageType('error');
            return;
        }

        // 必須項目のチェックをより厳密に
        if (!date || !type || !category || !amount || !description || !paymentMethod) { 
            setMessage('全ての必須項目を入力してください。');
            setMessageType('error');
            return;
        }

        if (isNaN(amount) || parseFloat(amount) <= 0) {
            setMessage('金額は正の数で入力してください。');
            setMessageType('error');
            return;
        }

        try {
            const transactionsColRef = collection(db, `artifacts/${firebaseAppId}/users/${userId}/transactions`);
            await addDoc(transactionsColRef, {
                userId,
                date: new Date(date), // 日付文字列をTimestampに変換
                type,
                category,
                amount: parseFloat(amount),
                description,
                paymentMethod,
                memo, // メモも保存
                createdAt: serverTimestamp() // 作成日時を記録
            });

            setMessage('取引が正常に記録されました！');
            setMessageType('success');
            
            // 入力フィールドをリセット
            setDate(new Date().toISOString().split('T')[0]);
            setType('支出');
            setCategory('');
            setAmount('');
            setDescription('');
            setPaymentMethod('現金');
            setMemo('');

        } catch (err) {
            console.error("取引の記録中にエラーが発生しました:", err);
            setMessage('取引の記録中にエラーが発生しました: ' + err.message);
            setMessageType('error');
        }
    };

    return (
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-2xl mx-auto border border-blue-200">
            <h2 className="text-3xl font-semibold text-gray-800 mb-6 border-b pb-4 text-center">新しい取引を入力</h2>
            
            {/* モバイル版のみ表示されるレシートスキャンボタン */}
            {isMobile && (
                <div className="mb-6">
                    <button
                        onClick={() => setCurrentPage('receiptScan')}
                        className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-4 rounded-md shadow-md transition duration-300 ease-in-out flex items-center justify-center space-x-2"
                    >
                        <span>レシートをスキャンする</span>
                    </button>
                </div>
            )}

            {message && (
                <div className={`p-4 rounded-md mb-4 ${messageType === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {message}
                </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                    <label htmlFor="date" className="block text-gray-700 text-sm font-bold mb-2">日付</label>
                    <input
                        type="date"
                        id="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        className="shadow appearance-none border rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-400"
                        required
                    />
                </div>
                <div>
                    <label htmlFor="type" className="block text-gray-700 text-sm font-bold mb-2">種類</label>
                    <select
                        id="type"
                        value={type}
                        onChange={(e) => { setType(e.target.value); setCategory(''); }} // 種類変更時に勘定科目をリセット
                        className="shadow border rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-400"
                        required
                    >
                        <option value="支出">支出</option>
                        <option value="収入">収入</option>
                    </select>
                </div>
                <div>
                    <label htmlFor="category" className="block text-gray-700 text-sm font-bold mb-2">勘定科目</label>
                    <select
                        id="category"
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        className="shadow border rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-400"
                        required
                    >
                        <option value="" disabled>選択してください</option>
                        {type === '支出' ? (
                            expenseCategories.map((cat) => (
                                <option key={cat} value={cat}>{cat}</option>
                            ))
                        ) : (
                            incomeCategories.map((cat) => (
                                <option key={cat} value={cat}>{cat}</option>
                            ))
                        )}
                    </select>
                </div>
                <div>
                    <label htmlFor="amount" className="block text-gray-700 text-sm font-bold mb-2">金額 (円)</label>
                    <input
                        type="number"
                        id="amount"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="shadow appearance-none border rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-400"
                        placeholder="例: 10000"
                        required
                        min="1"
                    />
                </div>
                <div>
                    <label htmlFor="description" className="block text-gray-700 text-sm font-bold mb-2">内容（詳細）</label>
                    <input
                        type="text"
                        id="description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="shadow appearance-none border rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-400"
                        placeholder="例: 文房具の購入"
                        required
                    />
                </div>
                <div>
                    <label htmlFor="paymentMethod" className="block text-gray-700 text-sm font-bold mb-2">支払い方法 / 入金元</label>
                    <select
                        id="paymentMethod"
                        value={paymentMethod}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="shadow border rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-400"
                        required
                    >
                        <option value="現金">現金</option>
                        <option value="普通預金">普通預金</option>
                        <option value="クレジットカード">クレジットカード</option>
                        <option value="売掛金">売掛金 (未回収)</option>
                        <option value="買掛金">買掛金 (未払い)</option>
                    </select>
                </div>
                <div>
                    <label htmlFor="memo" className="block text-gray-700 text-sm font-bold mb-2">メモ (任意)</label>
                    <textarea
                        id="memo"
                        value={memo}
                        onChange={(e) => setMemo(e.target.value)}
                        className="shadow appearance-none border rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-400"
                        rows="3"
                        placeholder="任意で詳細を記入"
                    ></textarea>
                </div>
                <button
                    type="submit"
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-4 rounded-lg focus:outline-none focus:shadow-outline transition duration-200 ease-in-out shadow-md"
                >
                    取引を記録
                </button>
            </form>
        </div>
    );
};

export default TransactionInput;
